/**
 * FR-9's rank heat scale: a logarithmic interpolation from rank 1 to rank 200+, anchored on the
 * six stops named in `decisions/the-rank-heat-scale-is-six-stops-on-a-log-ramp.md`.
 *
 * The pure function (`getRankHeat`) never touches the DOM. The six stops it interpolates between
 * live in `src/styles/tokens.css` and nowhere else — `getRankHeatStops` reads them at runtime
 * through `getComputedStyle` and memoises the parsed result, so there is no second, TypeScript-side
 * copy of the same six colour triples to drift from the CSS.
 *
 * Sponsored rank rides the same ramp in indigo: `getSponsoredRankHeatStops` reads a second
 * six-anchor family (`--heat-sp-rank-*`) and passes it to the same `getRankHeat`
 * (`decisions/sponsored-rank-shares-the-organic-log-ramp-in-indigo.md`).
 */

/** An `H S% L%` triple, unwrapped — the shape a `tokens.css` custom property carries. */
export type HSLTriple = [h: number, s: number, l: number];

export interface RankHeatStops {
  rank1: HSLTriple;
  rank10: HSLTriple;
  rank25: HSLTriple;
  rank50: HSLTriple;
  rank100: HSLTriple;
  rank200: HSLTriple;
}

export interface RankHeat {
  /** A ready-to-use `hsl(...)` CSS colour for the cell background. */
  background: string;
  /** A ready-to-use `hsl(var(--...))` CSS colour for the cell's text. */
  ink: string;
}

type StopKey = keyof RankHeatStops;

/** The six anchor ranks, in ascending order, paired with the stop they anchor. */
const ANCHORS: ReadonlyArray<{ rank: number; key: StopKey }> = [
  { rank: 1, key: "rank1" },
  { rank: 10, key: "rank10" },
  { rank: 25, key: "rank25" },
  { rank: 50, key: "rank50" },
  { rank: 100, key: "rank100" },
  { rank: 200, key: "rank200" },
];

const LOG_200 = Math.log(200);

/** `t(p) = ln(clamp(p, 1, 200)) / ln(200)` — `t(1) = 0`, `t(200) = 1`. */
function rampPosition(rank: number): number {
  const clamped = Math.min(Math.max(rank, 1), 200);
  return Math.log(clamped) / LOG_200;
}

/** `t` at each of the six anchor ranks, computed once with the same function as any other rank. */
const ANCHOR_T = ANCHORS.map((anchor) => rampPosition(anchor.rank));

function lerp(low: number, high: number, fraction: number): number {
  return low + (high - low) * fraction;
}

/**
 * The raw, unrounded `[h, s, l]` a rank paints — componentwise HSL interpolation between the two
 * bracketing stops at the local fraction of `t`. Ranks at or past 200 take the terminal stop
 * exactly, never an interpolated approach to it.
 */
function interpolateStop(stops: RankHeatStops, rank: number): HSLTriple {
  if (rank >= 200) {
    return stops.rank200;
  }

  const t = rampPosition(rank);

  for (let i = 0; i < ANCHORS.length - 1; i += 1) {
    const tLow = ANCHOR_T[i];
    const tHigh = ANCHOR_T[i + 1];
    if (t <= tHigh) {
      const low = stops[ANCHORS[i].key];
      const high = stops[ANCHORS[i + 1].key];
      const fraction = tHigh === tLow ? 0 : (t - tLow) / (tHigh - tLow);
      return [
        lerp(low[0], high[0], fraction),
        lerp(low[1], high[1], fraction),
        lerp(low[2], high[2], fraction),
      ];
    }
  }

  // Unreachable for any rank in [1, 200): ANCHOR_T[0] is 0 and ANCHOR_T[last] is 1, so the loop
  // above always finds a bracket. Kept only so the function has a total return type.
  return stops.rank200;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const S = s / 100;
  const L = l / 100;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const hPrime = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hPrime % 2) - 1));
  const m = L - c / 2;

  let r: number;
  let g: number;
  let b: number;
  if (hPrime < 1) {
    [r, g, b] = [c, x, 0];
  } else if (hPrime < 2) {
    [r, g, b] = [x, c, 0];
  } else if (hPrime < 3) {
    [r, g, b] = [0, c, x];
  } else if (hPrime < 4) {
    [r, g, b] = [0, x, c];
  } else if (hPrime < 5) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }

  return [r + m, g + m, b + m];
}

function channelLuminance(channel: number): number {
  return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance of an `H S% L%` colour. */
function relativeLuminance(h: number, s: number, l: number): number {
  const [r, g, b] = hslToRgb(h, s, l);
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

/** The luminance above which `--heat-on-light` contrasts better than `--heat-on-dark`. */
const INK_LUMINANCE_THRESHOLD = 0.21;

function round(value: number): number {
  return Math.round(value);
}

/**
 * Maps a rank to the colour it paints and the ink it should be read in.
 *
 * Pure: no DOM access, no `getComputedStyle`, no theme awareness. `stops` is the six-anchor table
 * `getRankHeatStops` reads from `tokens.css` — pass a fixture in tests.
 *
 * `position === null` is the wire's no-reading state (`decisions/the-rank-grid-is-one-brand-wide-read.md`)
 * and always takes the no-reading treatment, never a ramp colour. `0` and negative ranks are not a
 * state the wire can carry; this throws for them rather than silently painting the best colour.
 */
export function getRankHeat(position: number | null, stops: RankHeatStops): RankHeat {
  if (position === null) {
    return { background: "hsl(var(--muted))", ink: "hsl(var(--muted-foreground))" };
  }

  if (!Number.isFinite(position) || position < 1) {
    throw new RangeError(
      `getRankHeat: position must be a finite number >= 1, or null for no reading — got ${position}`,
    );
  }

  const [h, s, l] = interpolateStop(stops, position);
  const background = `hsl(${round(h)} ${round(s)}% ${round(l)}%)`;
  const luminance = relativeLuminance(h, s, l);
  const ink = luminance > INK_LUMINANCE_THRESHOLD ? "hsl(var(--heat-on-light))" : "hsl(var(--heat-on-dark))";

  return { background, ink };
}

/**
 * Parses one `tokens.css` custom property value of the form `"H S% L%"` into a numeric triple.
 * Exported separately from `getRankHeatStops` so the parse itself — the half of the reader with no
 * DOM in it — is directly testable.
 *
 * Throws on anything that does not match, including the empty string: that is what jsdom returns
 * for a custom property `tokens.css` never loaded, and what production returns if the heat family
 * is ever removed from the stylesheet. A silent `[0, 0, 0]` or a `NaN` triple would paint every cell
 * one wrong colour with a green test suite; throwing surfaces the missing token instead.
 */
export function parseHeatStopTriple(raw: string): HSLTriple {
  const trimmed = raw.trim();
  const match = trimmed.match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%$/);

  if (!match) {
    throw new Error(`parseHeatStopTriple: expected "H S% L%", got ${JSON.stringify(raw)}`);
  }

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

const HEAT_STOP_PROPERTIES: Record<StopKey, string> = {
  rank1: "--heat-rank-1",
  rank10: "--heat-rank-10",
  rank25: "--heat-rank-25",
  rank50: "--heat-rank-50",
  rank100: "--heat-rank-100",
  rank200: "--heat-rank-200",
};

/**
 * The indigo twin of `HEAT_STOP_PROPERTIES` — sponsored rank's six anchors
 * (`decisions/sponsored-rank-shares-the-organic-log-ramp-in-indigo.md`). Distinct from AUT-440's
 * `--heat-sp-3` / `--heat-sp-4` / `--heat-sp-on-light`, which are the Ad badge's colours and not a
 * ramp.
 */
const SPONSORED_HEAT_STOP_PROPERTIES: Record<StopKey, string> = {
  rank1: "--heat-sp-rank-1",
  rank10: "--heat-sp-rank-10",
  rank25: "--heat-sp-rank-25",
  rank50: "--heat-sp-rank-50",
  rank100: "--heat-sp-rank-100",
  rank200: "--heat-sp-rank-200",
};

function readHeatStops(properties: Record<StopKey, string>): RankHeatStops {
  const style = getComputedStyle(document.documentElement);
  const read = (key: StopKey): HSLTriple => parseHeatStopTriple(style.getPropertyValue(properties[key]));

  return {
    rank1: read("rank1"),
    rank10: read("rank10"),
    rank25: read("rank25"),
    rank50: read("rank50"),
    rank100: read("rank100"),
    rank200: read("rank200"),
  };
}

let memoisedStops: RankHeatStops | null = null;

/**
 * Reads the six heat stops from `tokens.css` via `getComputedStyle` on the document element, and
 * memoises the parsed result for the life of the page. Not a pure function — this is the only place
 * in the module that touches the DOM, kept separate from `getRankHeat` and `parseHeatStopTriple` so
 * both of those stay directly testable with no DOM at all.
 */
export function getRankHeatStops(): RankHeatStops {
  if (memoisedStops) {
    return memoisedStops;
  }

  memoisedStops = readHeatStops(HEAT_STOP_PROPERTIES);

  return memoisedStops;
}

/**
 * Its own memo, deliberately — **not** `memoisedStops`. The two readers return different families
 * from the same six keys, so one shared cache would hand whichever family was read first to both,
 * painting sponsored cells green or organic cells indigo with every test still green
 * (`decisions/sponsored-rank-shares-the-organic-log-ramp-in-indigo.md`).
 */
let memoisedSponsoredStops: RankHeatStops | null = null;

/**
 * The sponsored twin of `getRankHeatStops`: the same read, the same parse and the same page-lifetime
 * memo, over the indigo `--heat-sp-rank-*` family. The stops it returns feed `getRankHeat` unchanged
 * — sponsored rank is the same log ramp, the same interpolation and the same two inks, only in a
 * different hue.
 */
export function getSponsoredRankHeatStops(): RankHeatStops {
  if (memoisedSponsoredStops) {
    return memoisedSponsoredStops;
  }

  memoisedSponsoredStops = readHeatStops(SPONSORED_HEAT_STOP_PROPERTIES);

  return memoisedSponsoredStops;
}
